import {
  ActionIcon,
  Badge,
  Button,
  Group,
  NumberInput,
  Stack,
  Table,
  Text,
  TextInput
} from "@mantine/core"
import { PackageIcon, PlusIcon, TrashIcon } from "@phosphor-icons/react"
import { useTranslation } from "react-i18next"
import {
  CatalogVariantAutocomplete,
  type CatalogVariantPick,
} from "./CatalogVariantAutocomplete"

export type CatalogRow = {
  kind: "catalog"
  productId: string | null
  productLabel: string
  unit: string
  requestedQuantity: number | null
  unitCost: number | null
}

export type UncataloguedRow = {
  kind: "uncatalogued"
  uncataloguedItemName: string
  uncataloguedSku: string
  unit: string
  requestedQuantity: number | null
  unitCost: number | null
}

export type InboundRow = CatalogRow | UncataloguedRow

export const emptyCatalogRow = (): CatalogRow => ({
  kind: "catalog",
  productId: null,
  productLabel: "",
  unit: "ш",
  requestedQuantity: 1,
  unitCost: null,
})

export const emptyUncataloguedRow = (): UncataloguedRow => ({
  kind: "uncatalogued",
  uncataloguedItemName: "",
  uncataloguedSku: "",
  unit: "ш",
  requestedQuantity: 1,
  unitCost: null,
})

interface RowErrors {
  productLabel?: string | null
  uncataloguedItemName?: string | null
  unit?: string | null
  requestedQuantity?: string | null
  unitCost?: string | null
}

interface Props {
  rows: InboundRow[]
  errors: RowErrors[]
  allowUncatalogued: boolean
  onChange: (index: number, patch: Partial<CatalogRow> | Partial<UncataloguedRow>) => void
  onPickCatalog: (index: number, pick: CatalogVariantPick) => void
  onAddCatalog: () => void
  onAddUncatalogued: () => void
  onRemove: (index: number) => void
}

export function InboundItemsTable({
  rows,
  errors,
  allowUncatalogued,
  onChange,
  onPickCatalog,
  onAddCatalog,
  onAddUncatalogued,
  onRemove,
}: Props) {
  const { t } = useTranslation("inbound")
  const total = rows.reduce((sum, r) => {
    const qty = r.requestedQuantity ?? 0
    const cost = r.unitCost ?? 0
    return sum + qty * cost
  }, 0)

  return (
    <Stack gap="xs">
      <Table withTableBorder striped highlightOnHover={false}>
        <Table.Thead>
          <Table.Tr>
            <Table.Th w="45%">{t("itemsTable.colItem")}</Table.Th>
            <Table.Th w="12%">{t("itemsTable.colUnit")}</Table.Th>
            <Table.Th w="13%">{t("itemsTable.colQuantity")}</Table.Th>
            <Table.Th w="20%">{t("itemsTable.colPrice")}</Table.Th>
            <Table.Th w="10%"> </Table.Th>
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {rows.map((row, idx) => {
            const err = errors[idx] ?? {}
            return (
              <Table.Tr key={idx}>
                <Table.Td>
                  {row.kind === "catalog" ? (
                    <CatalogVariantAutocomplete
                      value={row.productLabel}
                      onChange={(label) =>
                        onChange(idx, { productLabel: label, productId: null })
                      }
                      onPick={(pick) => onPickCatalog(idx, pick)}
                      error={err.productLabel ?? null}
                    />
                  ) : (
                    <Group gap="xs" wrap="nowrap" align="flex-start">
                      <Badge size="xs" color="grape" variant="light">
                        {t("itemsTable.newBadge")}
                      </Badge>
                      <Stack gap="xs" flex={1}>
                        <TextInput
                          size="sm"
                          placeholder={t("itemsTable.itemNamePlaceholder")}
                          value={row.uncataloguedItemName}
                          error={err.uncataloguedItemName ?? undefined}
                          onChange={(e) =>
                            onChange(idx, {
                              uncataloguedItemName: e.currentTarget.value,
                            })
                          }
                        />
                        <TextInput
                          size="sm"
                          placeholder={t("itemsTable.skuPlaceholder")}
                          value={row.uncataloguedSku}
                          onChange={(e) =>
                            onChange(idx, {
                              uncataloguedSku: e.currentTarget.value,
                            })
                          }
                        />
                      </Stack>
                    </Group>
                  )}
                </Table.Td>
                <Table.Td>
                  <TextInput
                    size="sm"
                    value={row.unit}
                    error={err.unit ?? undefined}
                    onChange={(e) =>
                      onChange(idx, { unit: e.currentTarget.value })
                    }
                  />
                </Table.Td>
                <Table.Td>
                  <NumberInput
                    size="sm"
                    min={0}
                    decimalScale={2}
                    value={row.requestedQuantity ?? ""}
                    error={err.requestedQuantity ?? undefined}
                    onChange={(v) =>
                      onChange(idx, {
                        requestedQuantity: typeof v === "number" ? v : null,
                      })
                    }
                  />
                </Table.Td>
                <Table.Td>
                  <NumberInput
                    size="sm"
                    min={0}
                    decimalScale={2}
                    thousandSeparator=","
                    value={row.unitCost ?? ""}
                    error={err.unitCost ?? undefined}
                    onChange={(v) =>
                      onChange(idx, {
                        unitCost: typeof v === "number" ? v : null,
                      })
                    }
                  />
                </Table.Td>
                <Table.Td>
                  <ActionIcon
                    color="red"
                    variant="subtle"
                    disabled={rows.length === 1}
                    onClick={() => onRemove(idx)}
                    aria-label={t("itemsTable.removeRow")}
                  >
                    <TrashIcon size={14} />
                  </ActionIcon>
                </Table.Td>
              </Table.Tr>
            )
          })}
        </Table.Tbody>
      </Table>
      <Group justify="space-between" wrap="wrap" gap="xs">
        <Group gap="xs">
          <Button
            size="sm"
            variant="light"
            leftSection={<PlusIcon size={14} />}
            onClick={onAddCatalog}
          >
            {t("itemsTable.addItem")}
          </Button>
          {allowUncatalogued && (
            <Button
              size="sm"
              variant="light"
              color="grape"
              leftSection={<PlusIcon size={14} />}
              onClick={onAddUncatalogued}
            >
              {t("itemsTable.addUncatalogued")}
            </Button>
          )}
        </Group>
        <Group gap={6} c="dimmed">
          <PackageIcon size={14} />
          <Text size="sm">
            {t("itemsTable.totalSummary", { count: rows.length, total: total.toLocaleString("en-US") })}
          </Text>
        </Group>
      </Group>
    </Stack>
  )
}
